import {DependencyManager} from 'framework';
import {authClient} from '@/infrastructure/auth/auth-client';
import {rpcClient} from '@/infrastructure/rpc/rpc-client';
import {RpcAudioTranscriber} from '@/infrastructure/rpc/rpc-audio-transcriber';
import {LocalStorageAdapter} from '@/infrastructure/storage/local-storage.adapter';
import {BetterAuthAuthRepository} from '@/features/identity/infrastructure/better-auth.repository';
import {SignInWithEmail} from '@/features/identity/application/use-cases/sign-in-with-email';
import {SignUpWithEmail} from '@/features/identity/application/use-cases/sign-up-with-email';
import {ChangePassword} from '@/features/identity/application/use-cases/change-password';
import {RequestPasswordReset} from '@/features/identity/application/use-cases/request-password-reset';
import {ResetPassword} from '@/features/identity/application/use-cases/reset-password';
import {SignOut} from '@/features/identity/application/use-cases/sign-out';
import {GetSession} from '@/features/identity/application/use-cases/get-session';
import {AuthController} from '@/features/identity/presentation/auth.controller';
import {SignInWithEmailDataValidator} from '@/features/identity/application/validators/sign-in-with-email-data.validator';
import {SignUpWithEmailDataValidator} from '@/features/identity/application/validators/sign-up-with-email-data.validator';
import {ChangePasswordDataValidator} from '@/features/identity/application/validators/change-password-data.validator';
import {RequestPasswordResetDataValidator} from '@/features/identity/application/validators/request-password-reset-data.validator';
import {ResetPasswordDataValidator} from '@/features/identity/application/validators/reset-password-data.validator';
import {BetterAuthSignInResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-sign-in-response.validator';
import {BetterAuthSignUpResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-sign-up-response.validator';
import {BetterAuthSignOutResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-sign-out-response.validator';
import {BetterAuthGetSessionResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-get-session-response.validator';
import {BetterAuthChangePasswordResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-change-password-response.validator';
import {BetterAuthRequestPasswordResetResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-request-password-reset-response.validator';
import {BetterAuthResetPasswordResponseValidator} from '@/features/identity/infrastructure/validators/better-auth-reset-password-response.validator';
import {RpcDashboardRepository} from '@/features/dashboard/infrastructure/rpc/rpc-dashboard.repository';
import {DailyDashboardDtoToDailyDashboardMapper} from '@/features/dashboard/infrastructure/mappers/daily-dashboard-dto-to-daily-dashboard.mapper';
import {MealDetailDtoToMealDetailMapper} from '@/features/dashboard/infrastructure/mappers/meal-detail-dto-to-meal-detail.mapper';
import {CopyDay} from '@/features/dashboard/application/use-cases/copy-day';
import {CopyMeal} from '@/features/dashboard/application/use-cases/copy-meal';
import {CreateManualMeal} from '@/features/dashboard/application/use-cases/create-manual-meal';
import {DeleteMeal} from '@/features/dashboard/application/use-cases/delete-meal';
import {EditMeal} from '@/features/dashboard/application/use-cases/edit-meal';
import {GetDailyDashboard} from '@/features/dashboard/application/use-cases/get-daily-dashboard';
import {GetMealDetail} from '@/features/dashboard/application/use-cases/get-meal-detail';
import {DashboardController} from '@/features/dashboard/presentation/dashboard.controller';
import {CreateManualMealCommandValidator} from '@/features/dashboard/application/validators/create-manual-meal-command.validator';
import {ManualMealEntryDataValidator} from '@/features/dashboard/application/validators/manual-meal-entry-data.validator';
import {EditMealCommandValidator} from '@/features/dashboard/application/validators/edit-meal-command.validator';
import {DashboardQueryValidator} from '@/features/dashboard/application/validators/dashboard-query.validator';
import {KeyValueOnboardingStatusRepository} from '@/features/onboarding/infrastructure/storage/key-value-onboarding-status.repository';
import {CheckOnboardingStatus} from '@/features/onboarding/application/use-cases/check-onboarding-status';
import {CompleteOnboarding} from '@/features/onboarding/application/use-cases/complete-onboarding';
import {OnboardingController} from '@/features/onboarding/presentation/onboarding.controller';
import {ListProducts} from '@/features/user-products/application/use-cases/list-products';
import {ResolveProduct} from '@/features/user-products/application/use-cases/resolve-product';
import {CreateProductFromCatalog} from '@/features/user-products/application/use-cases/create-product-from-catalog';
import {SearchCatalogCandidates} from '@/features/user-products/application/use-cases/search-catalog-candidates';
import {CreateManualProduct} from '@/features/user-products/application/use-cases/create-manual-product';
import {UpdateProduct} from '@/features/user-products/application/use-cases/update-product';
import {DeleteProduct} from '@/features/user-products/application/use-cases/delete-product';
import {ScanNutritionLabel} from '@/features/user-products/application/use-cases/scan-nutrition-label';
import {CatalogCandidateDtoToCandidateMapper} from '@/features/user-products/infrastructure/mappers/catalog-candidate-dto-to-candidate.mapper';
import {ProductDtoToProductMapper} from '@/features/user-products/infrastructure/mappers/product-dto-to-product.mapper';
import {ResolveDtoToResolveResultMapper} from '@/features/user-products/infrastructure/mappers/resolve-dto-to-resolve-result.mapper';
import {RpcProductRepository} from '@/features/user-products/infrastructure/rpc/rpc-product.repository';
import {ProductController} from '@/features/user-products/presentation/product.controller';
import {RecordWeight} from '@/features/weight-tracking/application/use-cases/record-weight';
import {GetLatestWeight} from '@/features/weight-tracking/application/use-cases/get-latest-weight';
import {ListWeightEntries} from '@/features/weight-tracking/application/use-cases/list-weight-entries';
import {GetWeightSeries} from '@/features/weight-tracking/application/use-cases/get-weight-series';
import {UpdateWeightEntry} from '@/features/weight-tracking/application/use-cases/update-weight-entry';
import {DeleteWeightEntry} from '@/features/weight-tracking/application/use-cases/delete-weight-entry';
import {WeightEntryDtoToWeightEntryMapper} from '@/features/weight-tracking/infrastructure/mappers/weight-entry-dto-to-weight-entry.mapper';
import {RpcWeightTrackingRepository} from '@/features/weight-tracking/infrastructure/rpc/rpc-weight-tracking.repository';
import {WeightTrackingController} from '@/features/weight-tracking/presentation/weight-tracking.controller';
import {ListBodyMetrics} from '@/features/body-measurements/application/use-cases/list-body-metrics';
import {CreateBodyMetric} from '@/features/body-measurements/application/use-cases/create-body-metric';
import {UpdateBodyMetric} from '@/features/body-measurements/application/use-cases/update-body-metric';
import {DeleteBodyMetric} from '@/features/body-measurements/application/use-cases/delete-body-metric';
import {GetMeasurementsOverview} from '@/features/body-measurements/application/use-cases/get-measurements-overview';
import {GetMeasurementSession} from '@/features/body-measurements/application/use-cases/get-measurement-session';
import {SaveMeasurementSession} from '@/features/body-measurements/application/use-cases/save-measurement-session';
import {GetMeasurementSeries} from '@/features/body-measurements/application/use-cases/get-measurement-series';
import {UpdateMeasurementEntry} from '@/features/body-measurements/application/use-cases/update-measurement-entry';
import {DeleteMeasurementEntry} from '@/features/body-measurements/application/use-cases/delete-measurement-entry';
import {BodyMetricIdValidator} from '@/features/body-measurements/application/validators/body-metric-id.validator';
import {CustomMetricNameValidator} from '@/features/body-measurements/application/validators/custom-metric-name.validator';
import {MeasurementValueValidator} from '@/features/body-measurements/application/validators/measurement-value.validator';
import {MeasurementSessionValidator} from '@/features/body-measurements/application/validators/measurement-session.validator';
import {BodyMetricDtoToBodyMetricMapper} from '@/features/body-measurements/infrastructure/mappers/body-metric-dto-to-body-metric.mapper';
import {BodyMetricListItemDtoToBodyMetricWithEntryCountMapper} from '@/features/body-measurements/infrastructure/mappers/body-metric-list-item-dto-to-body-metric-with-entry-count.mapper';
import {MeasurementEntryDtoToMeasurementEntryMapper} from '@/features/body-measurements/infrastructure/mappers/measurement-entry-dto-to-measurement-entry.mapper';
import {OverviewItemDtoToMetricOverviewItemMapper} from '@/features/body-measurements/infrastructure/mappers/overview-item-dto-to-metric-overview-item.mapper';
import {RpcBodyMeasurementsRepository} from '@/features/body-measurements/infrastructure/rpc/rpc-body-measurements.repository';
import {BodyMeasurementsController} from '@/features/body-measurements/presentation/body-measurements.controller';
import {GetProfile as GetSettingsProfile} from '@/features/settings/application/use-cases/get-profile';
import {UpdateBodyMetrics} from '@/features/settings/application/use-cases/update-body-metrics';
import {UpdateAvatar} from '@/features/settings/application/use-cases/update-avatar';
import {DeleteAvatar} from '@/features/settings/application/use-cases/delete-avatar';
import {ListReminders as ListSettingsReminders} from '@/features/settings/application/use-cases/list-reminders';
import {ListDueReminders as ListSettingsDueReminders} from '@/features/settings/application/use-cases/list-due-reminders';
import {UpdateReminder as UpdateSettingsReminder} from '@/features/settings/application/use-cases/update-reminder';
import {AcknowledgeReminder as AcknowledgeSettingsReminder} from '@/features/settings/application/use-cases/acknowledge-reminder';
import {ReminderScheduleValidator} from '@/features/settings/application/validators/reminder-schedule.validator';
import {BodyMetricsValidator} from '@/features/settings/application/validators/body-metrics.validator';
import {ProfileDtoToUserProfileMapper} from '@/features/settings/infrastructure/mappers/profile-dto-to-user-profile.mapper';
import {ReminderDtoToUserReminderMapper} from '@/features/settings/infrastructure/mappers/reminder-dto-to-user-reminder.mapper';
import {RpcSettingsRepository} from '@/features/settings/infrastructure/rpc/rpc-settings.repository';
import {SettingsController} from '@/features/settings/presentation/settings.controller';
import {ListNutritionPlans} from '@/features/nutrition-plans/application/use-cases/list-nutrition-plans';
import {CreateNutritionPlan} from '@/features/nutrition-plans/application/use-cases/create-nutrition-plan';
import {UpdateNutritionPlan} from '@/features/nutrition-plans/application/use-cases/update-nutrition-plan';
import {DeleteNutritionPlan} from '@/features/nutrition-plans/application/use-cases/delete-nutrition-plan';
import {CreateNutritionPlanDataValidator} from '@/features/nutrition-plans/application/validators/create-nutrition-plan-data.validator';
import {UpdateNutritionPlanDataValidator} from '@/features/nutrition-plans/application/validators/update-nutrition-plan-data.validator';
import {NutritionPlanDtoToNutritionPlanMapper} from '@/features/nutrition-plans/infrastructure/mappers/nutrition-plan-dto-to-nutrition-plan.mapper';
import {RpcNutritionPlanRepository} from '@/features/nutrition-plans/infrastructure/rpc/rpc-nutrition-plan.repository';
import {NutritionPlanController} from '@/features/nutrition-plans/presentation/nutrition-plan.controller';
import {AddExerciseToWorkout} from '@/features/workouts/application/use-cases/add-exercise-to-workout';
import {ReplaceWorkoutExercise} from '@/features/workouts/application/use-cases/replace-workout-exercise';
import {DeleteSet} from '@/features/workouts/application/use-cases/delete-set';
import {RemoveExerciseFromWorkout} from '@/features/workouts/application/use-cases/remove-exercise-from-workout';
import {ReorderWorkoutExercises} from '@/features/workouts/application/use-cases/reorder-workout-exercises';
import {UpdateSet} from '@/features/workouts/application/use-cases/update-set';
import {CompleteWorkout} from '@/features/workouts/application/use-cases/complete-workout';
import {CreateWorkoutTemplate} from '@/features/workouts/application/use-cases/create-workout-template';
import {DeleteWorkoutTemplate} from '@/features/workouts/application/use-cases/delete-workout-template';
import {DiscardWorkout} from '@/features/workouts/application/use-cases/discard-workout';
import {DuplicateWorkoutTemplate} from '@/features/workouts/application/use-cases/duplicate-workout-template';
import {GetActiveWorkout} from '@/features/workouts/application/use-cases/get-active-workout';
import {GetFocusModePreference} from '@/features/workouts/application/use-cases/get-focus-mode-preference';
import {GetKeepScreenAwakePreference} from '@/features/workouts/application/use-cases/get-keep-screen-awake-preference';
import {GetCompletedWorkoutSeries} from '@/features/workouts/application/use-cases/get-completed-workout-series';
import {GetWorkout} from '@/features/workouts/application/use-cases/get-workout';
import {GetWorkoutTemplate} from '@/features/workouts/application/use-cases/get-workout-template';
import {CreateCustomExercise} from '@/features/workouts/application/use-cases/create-custom-exercise';
import {DeleteCustomExercise} from '@/features/workouts/application/use-cases/delete-custom-exercise';
import {GetCatalogExercise} from '@/features/workouts/application/use-cases/get-catalog-exercise';
import {ListCatalogExercises} from '@/features/workouts/application/use-cases/list-catalog-exercises';
import {ListCatalogMuscles} from '@/features/workouts/application/use-cases/list-catalog-muscles';
import {UpdateCustomExercise} from '@/features/workouts/application/use-cases/update-custom-exercise';
import {ListWorkoutTemplates} from '@/features/workouts/application/use-cases/list-workout-templates';
import {LogSet} from '@/features/workouts/application/use-cases/log-set';
import {ReplaceWorkoutTemplateExercises} from '@/features/workouts/application/use-cases/replace-workout-template-exercises';
import {SetFocusModePreference} from '@/features/workouts/application/use-cases/set-focus-mode-preference';
import {SetKeepScreenAwakePreference} from '@/features/workouts/application/use-cases/set-keep-screen-awake-preference';
import {StartWorkoutFromTemplate} from '@/features/workouts/application/use-cases/start-workout-from-template';
import {UpdateWorkoutTemplate} from '@/features/workouts/application/use-cases/update-workout-template';
import {AddExerciseToWorkoutDataValidator} from '@/features/workouts/application/validators/add-exercise-to-workout-data.validator';
import {SessionExerciseTargetsValidator} from '@/features/workouts/application/validators/session-exercise-targets.validator';
import {ClearRecentLineIngredients} from '@/features/saved-meals/application/use-cases/clear-recent-line-ingredients';
import {CreateSavedMeal} from '@/features/saved-meals/application/use-cases/create-saved-meal';
import {DeleteSavedMeal} from '@/features/saved-meals/application/use-cases/delete-saved-meal';
import {GetSavedMeal} from '@/features/saved-meals/application/use-cases/get-saved-meal';
import {ListRecentLineIngredients} from '@/features/saved-meals/application/use-cases/list-recent-line-ingredients';
import {ListSavedMeals} from '@/features/saved-meals/application/use-cases/list-saved-meals';
import {LookupLineIngredientByBarcode} from '@/features/saved-meals/application/use-cases/lookup-line-ingredient-by-barcode';
import {SearchLineIngredients} from '@/features/saved-meals/application/use-cases/search-line-ingredients';
import {TouchSavedMeal} from '@/features/saved-meals/application/use-cases/touch-saved-meal';
import {UpdateSavedMeal} from '@/features/saved-meals/application/use-cases/update-saved-meal';
import {CreateSavedMealDataValidator} from '@/features/saved-meals/application/validators/create-saved-meal-data.validator';
import {SavedMealIdValidator} from '@/features/saved-meals/application/validators/saved-meal-id.validator';
import {SavedMealLineDataValidator} from '@/features/saved-meals/application/validators/saved-meal-line-data.validator';
import {LineCandidateDtoToCandidateMapper} from '@/features/saved-meals/infrastructure/mappers/line-candidate-dto-to-candidate.mapper';
import {RecentItemDtoToRecentLineIngredientMapper} from '@/features/saved-meals/infrastructure/mappers/recent-item-dto-to-recent-line-ingredient.mapper';
import {SavedMealDtoToSavedMealMapper} from '@/features/saved-meals/infrastructure/mappers/saved-meal-dto-to-saved-meal.mapper';
import {SavedMealSummaryDtoToSummaryMapper} from '@/features/saved-meals/infrastructure/mappers/saved-meal-summary-dto-to-summary.mapper';
import {RpcSavedMealRepository} from '@/features/saved-meals/infrastructure/rpc/rpc-saved-meal.repository';
import {SavedMealController} from '@/features/saved-meals/presentation/saved-meal.controller';
import {CreateCustomExerciseDataValidator} from '@/features/workouts/application/validators/create-custom-exercise-data.validator';
import {ExerciseIdValidator} from '@/features/workouts/application/validators/exercise-id.validator';
import {CreateWorkoutTemplateDataValidator} from '@/features/workouts/application/validators/create-workout-template-data.validator';
import {LogSetDataValidator} from '@/features/workouts/application/validators/log-set-data.validator';
import {UpdateSetDataValidator} from '@/features/workouts/application/validators/update-set-data.validator';
import {UpdateWorkoutTemplateDataValidator} from '@/features/workouts/application/validators/update-workout-template-data.validator';
import {WorkoutIdValidator} from '@/features/workouts/application/validators/workout-id.validator';
import {WorkoutTemplateIdValidator} from '@/features/workouts/application/validators/workout-template-id.validator';
import {CatalogExerciseDtoToCatalogExerciseMapper} from '@/features/workouts/infrastructure/mappers/catalog-exercise-dto-to-catalog-exercise.mapper';
import {CatalogMuscleDtoToCatalogMuscleMapper} from '@/features/workouts/infrastructure/mappers/catalog-muscle-dto-to-catalog-muscle.mapper';
import {CompleteWorkoutDtoToResultMapper} from '@/features/workouts/infrastructure/mappers/complete-workout-dto-to-result.mapper';
import {ReplaceWorkoutExerciseDtoToResultMapper} from '@/features/workouts/infrastructure/mappers/replace-workout-exercise-dto-to-result.mapper';
import {LogSetDtoToLogSetResultMapper} from '@/features/workouts/infrastructure/mappers/log-set-dto-to-log-set-result.mapper';
import {WorkoutDtoToWorkoutMapper} from '@/features/workouts/infrastructure/mappers/workout-dto-to-workout.mapper';
import {WorkoutSummaryDtoToSummaryMapper} from '@/features/workouts/infrastructure/mappers/workout-summary-dto-to-summary.mapper';
import {WorkoutTemplateDtoToWorkoutTemplateMapper} from '@/features/workouts/infrastructure/mappers/workout-template-dto-to-workout-template.mapper';
import {WorkoutTemplateSummaryDtoToSummaryMapper} from '@/features/workouts/infrastructure/mappers/workout-template-summary-dto-to-summary.mapper';
import {RpcExerciseCatalogRepository} from '@/features/workouts/infrastructure/rpc/rpc-exercise-catalog.repository';
import {KeyValueFocusModePreferenceRepository} from '@/features/workouts/infrastructure/storage/key-value-focus-mode-preference.repository';
import {KeyValueKeepScreenAwakePreferenceRepository} from '@/features/workouts/infrastructure/storage/key-value-keep-screen-awake-preference.repository';
import {RpcWorkoutRepository} from '@/features/workouts/infrastructure/rpc/rpc-workout.repository';
import {WorkoutController} from '@/features/workouts/presentation/workout.controller';
import {ListConversations} from '@/features/assistant/application/use-cases/list-conversations';
import {GetConversation} from '@/features/assistant/application/use-cases/get-conversation';
import {CreateConversation} from '@/features/assistant/application/use-cases/create-conversation';
import {DeleteConversation} from '@/features/assistant/application/use-cases/delete-conversation';
import {ApplyProposal} from '@/features/assistant/application/use-cases/apply-proposal';
import {DiscardProposal} from '@/features/assistant/application/use-cases/discard-proposal';
import {UploadAttachment} from '@/features/assistant/application/use-cases/upload-attachment';
import {DeleteAttachment} from '@/features/assistant/application/use-cases/delete-attachment';
import {ConversationIdValidator} from '@/features/assistant/application/validators/conversation-id.validator';
import {SendMessageDataValidator} from '@/features/assistant/application/validators/send-message-data.validator';
import {ConversationDtoToConversationMapper} from '@/features/assistant/infrastructure/mappers/conversation-dto-to-conversation.mapper';
import {AssistantMessageDtoToAssistantMessageMapper} from '@/features/assistant/infrastructure/mappers/assistant-message-dto-to-assistant-message.mapper';
import {AssistantProposalDtoToAssistantProposalMapper} from '@/features/assistant/infrastructure/mappers/assistant-proposal-dto-to-assistant-proposal.mapper';
import {AssistantAttachmentDtoToAssistantAttachmentMapper} from '@/features/assistant/infrastructure/mappers/assistant-attachment-dto-to-assistant-attachment.mapper';
import {AssistantAttachmentToImageBucketImageMapper} from '@/features/assistant/infrastructure/mappers/assistant-attachment-to-image-bucket-image.mapper';
import {ConversationDetailDtoToConversationDetailMapper} from '@/features/assistant/infrastructure/mappers/conversation-detail-dto-to-conversation-detail.mapper';
import {RpcAssistantRepository} from '@/features/assistant/infrastructure/rpc/rpc-assistant.repository';
import {AssistantController} from '@/features/assistant/presentation/assistant.controller';
import {GetScheduleCalendar} from '@/features/schedule/application/use-cases/get-schedule-calendar';
import {GetScheduleWeek} from '@/features/schedule/application/use-cases/get-schedule-week';
import {SaveScheduleWeek} from '@/features/schedule/application/use-cases/save-schedule-week';
import {CreateScheduleOneOff} from '@/features/schedule/application/use-cases/create-schedule-one-off';
import {RemoveScheduleOneOff} from '@/features/schedule/application/use-cases/remove-schedule-one-off';
import {SkipScheduleOccurrence} from '@/features/schedule/application/use-cases/skip-schedule-occurrence';
import {RestoreScheduleOccurrence} from '@/features/schedule/application/use-cases/restore-schedule-occurrence';
import {MoveScheduleOccurrence} from '@/features/schedule/application/use-cases/move-schedule-occurrence';
import {SwapScheduleOccurrence} from '@/features/schedule/application/use-cases/swap-schedule-occurrence';
import {StartScheduleOccurrence} from '@/features/schedule/application/use-cases/start-schedule-occurrence';
import {ListScheduleTemplates} from '@/features/schedule/application/use-cases/list-schedule-templates';
import {GetActiveScheduleWorkout} from '@/features/schedule/application/use-cases/get-active-schedule-workout';
import {ScheduleCalendarDtoToScheduleCalendarMapper} from '@/features/schedule/infrastructure/mappers/schedule-calendar-dto-to-schedule-calendar.mapper';
import {ScheduleWeekSlotDtoToScheduleWeekSlotMapper} from '@/features/schedule/infrastructure/mappers/schedule-week-slot-dto-to-schedule-week-slot.mapper';
import {WorkoutTemplateSummaryDtoToScheduleTemplateOptionMapper} from '@/features/schedule/infrastructure/mappers/workout-template-summary-dto-to-schedule-template-option.mapper';
import {WorkoutDtoToActiveScheduleWorkoutMapper} from '@/features/schedule/infrastructure/mappers/workout-dto-to-active-schedule-workout.mapper';
import {RpcScheduleRepository} from '@/features/schedule/infrastructure/rpc/rpc-schedule.repository';
import {ScheduleController} from '@/features/schedule/presentation/schedule.controller';

const dependencyManager = new DependencyManager()
  .authenticators(() => ({
    betterAuthAuthRepository: new BetterAuthAuthRepository(
      authClient,
      new BetterAuthSignInResponseValidator(),
      new BetterAuthSignUpResponseValidator(),
      new BetterAuthSignOutResponseValidator(),
      new BetterAuthGetSessionResponseValidator(),
      new BetterAuthChangePasswordResponseValidator(),
      new BetterAuthRequestPasswordResetResponseValidator(),
      new BetterAuthResetPasswordResponseValidator(),
    ),
  }))
  .validators(() => ({
    signInWithEmailDataValidator: new SignInWithEmailDataValidator(),
    signUpWithEmailDataValidator: new SignUpWithEmailDataValidator(),
    changePasswordDataValidator: new ChangePasswordDataValidator(),
    requestPasswordResetDataValidator: new RequestPasswordResetDataValidator(),
    resetPasswordDataValidator: new ResetPasswordDataValidator(),
    dashboardQueryValidator: new DashboardQueryValidator(),
    editMealCommandValidator: new EditMealCommandValidator(),
    createManualMealCommandValidator: new CreateManualMealCommandValidator(),
    manualMealEntryDataValidator: new ManualMealEntryDataValidator(),
    bodyMetricsValidator: new BodyMetricsValidator(),
    reminderScheduleValidator: new ReminderScheduleValidator(),
    bodyMetricIdValidator: new BodyMetricIdValidator(),
    customMetricNameValidator: new CustomMetricNameValidator(),
    measurementValueValidator: new MeasurementValueValidator(),
    measurementSessionValidator: new MeasurementSessionValidator(),
    createNutritionPlanDataValidator: new CreateNutritionPlanDataValidator(),
    updateNutritionPlanDataValidator: new UpdateNutritionPlanDataValidator(),
    createWorkoutTemplateDataValidator: new CreateWorkoutTemplateDataValidator(),
    updateWorkoutTemplateDataValidator: new UpdateWorkoutTemplateDataValidator(),
    createCustomExerciseDataValidator: new CreateCustomExerciseDataValidator(),
    exerciseIdValidator: new ExerciseIdValidator(),
    workoutTemplateIdValidator: new WorkoutTemplateIdValidator(),
    workoutIdValidator: new WorkoutIdValidator(),
    logSetDataValidator: new LogSetDataValidator(),
    updateSetDataValidator: new UpdateSetDataValidator(),
    addExerciseToWorkoutDataValidator: new AddExerciseToWorkoutDataValidator(),
    sessionExerciseTargetsValidator: new SessionExerciseTargetsValidator(),
    createSavedMealDataValidator: new CreateSavedMealDataValidator(),
    savedMealLineDataValidator: new SavedMealLineDataValidator(),
    savedMealIdValidator: new SavedMealIdValidator(),
    conversationIdValidator: new ConversationIdValidator(),
    sendMessageDataValidator: new SendMessageDataValidator(),
  }))
  .mappers(() => {
    const productDtoToProductMapper = new ProductDtoToProductMapper();
    const bodyMetricDtoToBodyMetricMapper = new BodyMetricDtoToBodyMetricMapper();
    const measurementEntryDtoToMeasurementEntryMapper =
      new MeasurementEntryDtoToMeasurementEntryMapper();
    return {
      dailyDashboardDtoToDailyDashboardMapper: new DailyDashboardDtoToDailyDashboardMapper(),
      mealDetailDtoToMealDetailMapper: new MealDetailDtoToMealDetailMapper(),
      productDtoToProductMapper,
      resolveDtoToResolveResultMapper: new ResolveDtoToResolveResultMapper(
        productDtoToProductMapper,
      ),
      catalogCandidateDtoToCandidateMapper: new CatalogCandidateDtoToCandidateMapper(
        productDtoToProductMapper,
      ),
      weightEntryDtoToWeightEntryMapper: new WeightEntryDtoToWeightEntryMapper(),
      bodyMetricDtoToBodyMetricMapper,
      bodyMetricListItemDtoToBodyMetricWithEntryCountMapper:
        new BodyMetricListItemDtoToBodyMetricWithEntryCountMapper(bodyMetricDtoToBodyMetricMapper),
      measurementEntryDtoToMeasurementEntryMapper,
      overviewItemDtoToMetricOverviewItemMapper: new OverviewItemDtoToMetricOverviewItemMapper(
        bodyMetricDtoToBodyMetricMapper,
        measurementEntryDtoToMeasurementEntryMapper,
      ),
      profileDtoToUserProfileMapper: new ProfileDtoToUserProfileMapper(),
      reminderDtoToUserReminderMapper: new ReminderDtoToUserReminderMapper(),
      nutritionPlanDtoToNutritionPlanMapper: new NutritionPlanDtoToNutritionPlanMapper(),
      workoutTemplateDtoToWorkoutTemplateMapper: new WorkoutTemplateDtoToWorkoutTemplateMapper(),
      workoutTemplateSummaryDtoToSummaryMapper: new WorkoutTemplateSummaryDtoToSummaryMapper(),
      workoutDtoToWorkoutMapper: new WorkoutDtoToWorkoutMapper(),
      workoutSummaryDtoToSummaryMapper: new WorkoutSummaryDtoToSummaryMapper(),
      catalogExerciseDtoToCatalogExerciseMapper: new CatalogExerciseDtoToCatalogExerciseMapper(),
      catalogMuscleDtoToCatalogMuscleMapper: new CatalogMuscleDtoToCatalogMuscleMapper(),
      savedMealDtoToSavedMealMapper: new SavedMealDtoToSavedMealMapper(),
      savedMealSummaryDtoToSummaryMapper: new SavedMealSummaryDtoToSummaryMapper(),
      lineCandidateDtoToCandidateMapper: new LineCandidateDtoToCandidateMapper(),
      recentItemDtoToRecentLineIngredientMapper: new RecentItemDtoToRecentLineIngredientMapper(),
      logSetDtoToLogSetResultMapper: new LogSetDtoToLogSetResultMapper(
        new WorkoutDtoToWorkoutMapper(),
      ),
      completeWorkoutDtoToResultMapper: new CompleteWorkoutDtoToResultMapper(
        new WorkoutDtoToWorkoutMapper(),
      ),
      replaceWorkoutExerciseDtoToResultMapper: new ReplaceWorkoutExerciseDtoToResultMapper(
        new WorkoutDtoToWorkoutMapper(),
      ),
      conversationDtoToConversationMapper: new ConversationDtoToConversationMapper(),
      assistantProposalDtoToAssistantProposalMapper:
        new AssistantProposalDtoToAssistantProposalMapper(),
      assistantAttachmentDtoToAssistantAttachmentMapper:
        new AssistantAttachmentDtoToAssistantAttachmentMapper(),
      assistantAttachmentToImageBucketImageMapper:
        new AssistantAttachmentToImageBucketImageMapper(),
      assistantMessageDtoToAssistantMessageMapper: new AssistantMessageDtoToAssistantMessageMapper(
        new AssistantProposalDtoToAssistantProposalMapper(),
        new AssistantAttachmentDtoToAssistantAttachmentMapper(),
      ),
      conversationDetailDtoToConversationDetailMapper:
        new ConversationDetailDtoToConversationDetailMapper(
          new ConversationDtoToConversationMapper(),
          new AssistantMessageDtoToAssistantMessageMapper(
            new AssistantProposalDtoToAssistantProposalMapper(),
            new AssistantAttachmentDtoToAssistantAttachmentMapper(),
          ),
        ),
      scheduleCalendarDtoToScheduleCalendarMapper:
        new ScheduleCalendarDtoToScheduleCalendarMapper(),
      scheduleWeekSlotDtoToScheduleWeekSlotMapper:
        new ScheduleWeekSlotDtoToScheduleWeekSlotMapper(),
      workoutTemplateSummaryDtoToScheduleTemplateOptionMapper:
        new WorkoutTemplateSummaryDtoToScheduleTemplateOptionMapper(),
      workoutDtoToActiveScheduleWorkoutMapper: new WorkoutDtoToActiveScheduleWorkoutMapper(),
    };
  })
  .repositories(({mappers}) => ({
    dashboardRepository: new RpcDashboardRepository(
      rpcClient,
      mappers.dailyDashboardDtoToDailyDashboardMapper,
      mappers.mealDetailDtoToMealDetailMapper,
    ),
    onboardingStatusRepository: new KeyValueOnboardingStatusRepository(new LocalStorageAdapter()),
    audioTranscriber: new RpcAudioTranscriber(rpcClient),
    productRepository: new RpcProductRepository(
      rpcClient,
      mappers.productDtoToProductMapper,
      mappers.resolveDtoToResolveResultMapper,
      mappers.catalogCandidateDtoToCandidateMapper,
    ),
    weightTrackingRepository: new RpcWeightTrackingRepository(
      rpcClient,
      mappers.weightEntryDtoToWeightEntryMapper,
    ),
    bodyMeasurementsRepository: new RpcBodyMeasurementsRepository(
      rpcClient,
      mappers.bodyMetricDtoToBodyMetricMapper,
      mappers.bodyMetricListItemDtoToBodyMetricWithEntryCountMapper,
      mappers.measurementEntryDtoToMeasurementEntryMapper,
      mappers.overviewItemDtoToMetricOverviewItemMapper,
    ),
    settingsRepository: new RpcSettingsRepository(
      rpcClient,
      mappers.profileDtoToUserProfileMapper,
      mappers.reminderDtoToUserReminderMapper,
    ),
    nutritionPlanRepository: new RpcNutritionPlanRepository(
      rpcClient,
      mappers.nutritionPlanDtoToNutritionPlanMapper,
    ),
    workoutRepository: new RpcWorkoutRepository(
      rpcClient,
      mappers.workoutTemplateDtoToWorkoutTemplateMapper,
      mappers.workoutTemplateSummaryDtoToSummaryMapper,
      mappers.workoutDtoToWorkoutMapper,
      mappers.workoutSummaryDtoToSummaryMapper,
      mappers.logSetDtoToLogSetResultMapper,
      mappers.completeWorkoutDtoToResultMapper,
      mappers.replaceWorkoutExerciseDtoToResultMapper,
    ),
    exerciseCatalogRepository: new RpcExerciseCatalogRepository(
      rpcClient,
      mappers.catalogExerciseDtoToCatalogExerciseMapper,
      mappers.catalogMuscleDtoToCatalogMuscleMapper,
    ),
    savedMealRepository: new RpcSavedMealRepository(
      rpcClient,
      mappers.savedMealDtoToSavedMealMapper,
      mappers.savedMealSummaryDtoToSummaryMapper,
      mappers.lineCandidateDtoToCandidateMapper,
      mappers.recentItemDtoToRecentLineIngredientMapper,
    ),
    focusModePreferenceRepository: new KeyValueFocusModePreferenceRepository(
      new LocalStorageAdapter(),
    ),
    keepScreenAwakePreferenceRepository: new KeyValueKeepScreenAwakePreferenceRepository(
      new LocalStorageAdapter(),
    ),
    assistantRepository: new RpcAssistantRepository(
      rpcClient,
      mappers.conversationDtoToConversationMapper,
      mappers.conversationDetailDtoToConversationDetailMapper,
      mappers.assistantProposalDtoToAssistantProposalMapper,
      mappers.assistantAttachmentDtoToAssistantAttachmentMapper,
    ),
    scheduleRepository: new RpcScheduleRepository(
      rpcClient,
      mappers.scheduleCalendarDtoToScheduleCalendarMapper,
      mappers.scheduleWeekSlotDtoToScheduleWeekSlotMapper,
      mappers.workoutTemplateSummaryDtoToScheduleTemplateOptionMapper,
      mappers.workoutDtoToActiveScheduleWorkoutMapper,
    ),
  }))
  .useCases(({authenticators, repositories, validators}) => ({
    signInWithEmail: new SignInWithEmail(authenticators.betterAuthAuthRepository),
    signUpWithEmail: new SignUpWithEmail(authenticators.betterAuthAuthRepository),
    changePassword: new ChangePassword(authenticators.betterAuthAuthRepository),
    requestPasswordReset: new RequestPasswordReset(authenticators.betterAuthAuthRepository),
    resetPassword: new ResetPassword(authenticators.betterAuthAuthRepository),
    signOut: new SignOut(authenticators.betterAuthAuthRepository),
    getSession: new GetSession(authenticators.betterAuthAuthRepository),
    getDailyDashboard: new GetDailyDashboard(repositories.dashboardRepository),
    getMealDetail: new GetMealDetail(repositories.dashboardRepository),
    deleteMeal: new DeleteMeal(repositories.dashboardRepository),
    editMeal: new EditMeal(repositories.dashboardRepository, validators.editMealCommandValidator),
    createManualMeal: new CreateManualMeal(
      repositories.dashboardRepository,
      validators.createManualMealCommandValidator,
    ),
    copyMeal: new CopyMeal(repositories.dashboardRepository),
    copyDay: new CopyDay(repositories.dashboardRepository),
    checkOnboardingStatus: new CheckOnboardingStatus(repositories.onboardingStatusRepository),
    completeOnboarding: new CompleteOnboarding(repositories.onboardingStatusRepository),
    listProducts: new ListProducts(repositories.productRepository),
    resolveProduct: new ResolveProduct(repositories.productRepository),
    searchCatalogCandidates: new SearchCatalogCandidates(repositories.productRepository),
    createProductFromCatalog: new CreateProductFromCatalog(repositories.productRepository),
    createManualProduct: new CreateManualProduct(repositories.productRepository),
    updateProduct: new UpdateProduct(repositories.productRepository),
    deleteProduct: new DeleteProduct(repositories.productRepository),
    scanNutritionLabel: new ScanNutritionLabel(repositories.productRepository),
    recordWeight: new RecordWeight(repositories.weightTrackingRepository),
    getLatestWeight: new GetLatestWeight(repositories.weightTrackingRepository),
    listWeightEntries: new ListWeightEntries(repositories.weightTrackingRepository),
    getWeightSeries: new GetWeightSeries(repositories.weightTrackingRepository),
    updateWeightEntry: new UpdateWeightEntry(repositories.weightTrackingRepository),
    deleteWeightEntry: new DeleteWeightEntry(repositories.weightTrackingRepository),
    listBodyMetrics: new ListBodyMetrics(repositories.bodyMeasurementsRepository),
    createBodyMetric: new CreateBodyMetric(repositories.bodyMeasurementsRepository),
    updateBodyMetric: new UpdateBodyMetric(repositories.bodyMeasurementsRepository),
    deleteBodyMetric: new DeleteBodyMetric(repositories.bodyMeasurementsRepository),
    getMeasurementsOverview: new GetMeasurementsOverview(repositories.bodyMeasurementsRepository),
    getMeasurementSession: new GetMeasurementSession(repositories.bodyMeasurementsRepository),
    saveMeasurementSession: new SaveMeasurementSession(repositories.bodyMeasurementsRepository),
    getMeasurementSeries: new GetMeasurementSeries(repositories.bodyMeasurementsRepository),
    updateMeasurementEntry: new UpdateMeasurementEntry(repositories.bodyMeasurementsRepository),
    deleteMeasurementEntry: new DeleteMeasurementEntry(repositories.bodyMeasurementsRepository),
    getSettingsProfile: new GetSettingsProfile(repositories.settingsRepository),
    updateBodyMetrics: new UpdateBodyMetrics(repositories.settingsRepository),
    updateAvatar: new UpdateAvatar(repositories.settingsRepository),
    deleteAvatar: new DeleteAvatar(repositories.settingsRepository),
    listSettingsReminders: new ListSettingsReminders(repositories.settingsRepository),
    listSettingsDueReminders: new ListSettingsDueReminders(repositories.settingsRepository),
    updateSettingsReminder: new UpdateSettingsReminder(repositories.settingsRepository),
    acknowledgeSettingsReminder: new AcknowledgeSettingsReminder(repositories.settingsRepository),
    listNutritionPlans: new ListNutritionPlans(repositories.nutritionPlanRepository),
    createNutritionPlan: new CreateNutritionPlan(
      repositories.nutritionPlanRepository,
      validators.createNutritionPlanDataValidator,
    ),
    updateNutritionPlan: new UpdateNutritionPlan(
      repositories.nutritionPlanRepository,
      validators.updateNutritionPlanDataValidator,
    ),
    deleteNutritionPlan: new DeleteNutritionPlan(repositories.nutritionPlanRepository),
    listWorkoutTemplates: new ListWorkoutTemplates(repositories.workoutRepository),
    getWorkoutTemplate: new GetWorkoutTemplate(repositories.workoutRepository),
    createWorkoutTemplate: new CreateWorkoutTemplate(
      repositories.workoutRepository,
      validators.createWorkoutTemplateDataValidator,
    ),
    updateWorkoutTemplate: new UpdateWorkoutTemplate(
      repositories.workoutRepository,
      validators.updateWorkoutTemplateDataValidator,
    ),
    replaceWorkoutTemplateExercises: new ReplaceWorkoutTemplateExercises(
      repositories.workoutRepository,
    ),
    deleteWorkoutTemplate: new DeleteWorkoutTemplate(repositories.workoutRepository),
    duplicateWorkoutTemplate: new DuplicateWorkoutTemplate(repositories.workoutRepository),
    getActiveWorkout: new GetActiveWorkout(repositories.workoutRepository),
    getWorkout: new GetWorkout(repositories.workoutRepository),
    getCompletedWorkoutSeries: new GetCompletedWorkoutSeries(repositories.workoutRepository),
    startWorkoutFromTemplate: new StartWorkoutFromTemplate(repositories.workoutRepository),
    discardWorkout: new DiscardWorkout(repositories.workoutRepository),
    logSet: new LogSet(repositories.workoutRepository, validators.logSetDataValidator),
    updateSet: new UpdateSet(repositories.workoutRepository),
    deleteSet: new DeleteSet(repositories.workoutRepository),
    reorderWorkoutExercises: new ReorderWorkoutExercises(repositories.workoutRepository),
    removeExerciseFromWorkout: new RemoveExerciseFromWorkout(repositories.workoutRepository),
    completeWorkout: new CompleteWorkout(repositories.workoutRepository),
    addExerciseToWorkout: new AddExerciseToWorkout(
      repositories.workoutRepository,
      validators.addExerciseToWorkoutDataValidator,
    ),
    replaceWorkoutExercise: new ReplaceWorkoutExercise(repositories.workoutRepository),
    listCatalogExercises: new ListCatalogExercises(repositories.exerciseCatalogRepository),
    listCatalogMuscles: new ListCatalogMuscles(repositories.exerciseCatalogRepository),
    getCatalogExercise: new GetCatalogExercise(repositories.exerciseCatalogRepository),
    createCustomExercise: new CreateCustomExercise(repositories.exerciseCatalogRepository),
    updateCustomExercise: new UpdateCustomExercise(repositories.exerciseCatalogRepository),
    deleteCustomExercise: new DeleteCustomExercise(repositories.exerciseCatalogRepository),
    getFocusModePreference: new GetFocusModePreference(repositories.focusModePreferenceRepository),
    setFocusModePreference: new SetFocusModePreference(repositories.focusModePreferenceRepository),
    getKeepScreenAwakePreference: new GetKeepScreenAwakePreference(
      repositories.keepScreenAwakePreferenceRepository,
    ),
    setKeepScreenAwakePreference: new SetKeepScreenAwakePreference(
      repositories.keepScreenAwakePreferenceRepository,
    ),
    listSavedMeals: new ListSavedMeals(repositories.savedMealRepository),
    getSavedMeal: new GetSavedMeal(repositories.savedMealRepository),
    createSavedMeal: new CreateSavedMeal(repositories.savedMealRepository),
    updateSavedMeal: new UpdateSavedMeal(repositories.savedMealRepository),
    deleteSavedMeal: new DeleteSavedMeal(repositories.savedMealRepository),
    touchSavedMeal: new TouchSavedMeal(repositories.savedMealRepository),
    searchLineIngredients: new SearchLineIngredients(repositories.savedMealRepository),
    lookupLineIngredientByBarcode: new LookupLineIngredientByBarcode(
      repositories.savedMealRepository,
    ),
    listRecentLineIngredients: new ListRecentLineIngredients(repositories.savedMealRepository),
    clearRecentLineIngredients: new ClearRecentLineIngredients(repositories.savedMealRepository),
    listConversations: new ListConversations(repositories.assistantRepository),
    getConversation: new GetConversation(repositories.assistantRepository),
    createConversation: new CreateConversation(repositories.assistantRepository),
    deleteConversation: new DeleteConversation(repositories.assistantRepository),
    applyProposal: new ApplyProposal(repositories.assistantRepository),
    discardProposal: new DiscardProposal(repositories.assistantRepository),
    uploadAttachment: new UploadAttachment(repositories.assistantRepository),
    deleteAttachment: new DeleteAttachment(repositories.assistantRepository),
    getScheduleCalendar: new GetScheduleCalendar(repositories.scheduleRepository),
    getScheduleWeek: new GetScheduleWeek(repositories.scheduleRepository),
    saveScheduleWeek: new SaveScheduleWeek(repositories.scheduleRepository),
    createScheduleOneOff: new CreateScheduleOneOff(repositories.scheduleRepository),
    removeScheduleOneOff: new RemoveScheduleOneOff(repositories.scheduleRepository),
    skipScheduleOccurrence: new SkipScheduleOccurrence(repositories.scheduleRepository),
    restoreScheduleOccurrence: new RestoreScheduleOccurrence(repositories.scheduleRepository),
    moveScheduleOccurrence: new MoveScheduleOccurrence(repositories.scheduleRepository),
    swapScheduleOccurrence: new SwapScheduleOccurrence(repositories.scheduleRepository),
    startScheduleOccurrence: new StartScheduleOccurrence(repositories.scheduleRepository),
    listScheduleTemplates: new ListScheduleTemplates(repositories.scheduleRepository),
    getActiveScheduleWorkout: new GetActiveScheduleWorkout(repositories.scheduleRepository),
  }))
  .controllers(({useCases}) => ({
    authController: new AuthController(
      useCases.signInWithEmail,
      useCases.signUpWithEmail,
      useCases.changePassword,
      useCases.requestPasswordReset,
      useCases.resetPassword,
      useCases.signOut,
      useCases.getSession,
    ),
    dashboardController: new DashboardController(
      useCases.getDailyDashboard,
      useCases.getMealDetail,
      useCases.deleteMeal,
      useCases.editMeal,
      useCases.createManualMeal,
      useCases.copyMeal,
      useCases.copyDay,
    ),
    onboardingController: new OnboardingController(
      useCases.checkOnboardingStatus,
      useCases.completeOnboarding,
    ),
    productController: new ProductController(
      useCases.listProducts,
      useCases.resolveProduct,
      useCases.searchCatalogCandidates,
      useCases.createProductFromCatalog,
      useCases.createManualProduct,
      useCases.updateProduct,
      useCases.deleteProduct,
      useCases.scanNutritionLabel,
    ),
    weightTrackingController: new WeightTrackingController(
      useCases.recordWeight,
      useCases.updateWeightEntry,
      useCases.deleteWeightEntry,
      useCases.getLatestWeight,
      useCases.listWeightEntries,
      useCases.getWeightSeries,
    ),
    bodyMeasurementsController: new BodyMeasurementsController(
      useCases.listBodyMetrics,
      useCases.createBodyMetric,
      useCases.updateBodyMetric,
      useCases.deleteBodyMetric,
      useCases.getMeasurementsOverview,
      useCases.getMeasurementSession,
      useCases.saveMeasurementSession,
      useCases.getMeasurementSeries,
      useCases.updateMeasurementEntry,
      useCases.deleteMeasurementEntry,
    ),
    settingsController: new SettingsController(
      useCases.getSettingsProfile,
      useCases.updateBodyMetrics,
      useCases.updateAvatar,
      useCases.deleteAvatar,
      useCases.listSettingsReminders,
      useCases.listSettingsDueReminders,
      useCases.updateSettingsReminder,
      useCases.acknowledgeSettingsReminder,
    ),
    nutritionPlanController: new NutritionPlanController(
      useCases.listNutritionPlans,
      useCases.createNutritionPlan,
      useCases.updateNutritionPlan,
      useCases.deleteNutritionPlan,
    ),
    savedMealController: new SavedMealController(
      useCases.listSavedMeals,
      useCases.getSavedMeal,
      useCases.createSavedMeal,
      useCases.updateSavedMeal,
      useCases.deleteSavedMeal,
      useCases.touchSavedMeal,
      useCases.searchLineIngredients,
      useCases.lookupLineIngredientByBarcode,
      useCases.listRecentLineIngredients,
      useCases.clearRecentLineIngredients,
    ),
    workoutController: new WorkoutController(
      useCases.listWorkoutTemplates,
      useCases.getWorkoutTemplate,
      useCases.createWorkoutTemplate,
      useCases.updateWorkoutTemplate,
      useCases.replaceWorkoutTemplateExercises,
      useCases.deleteWorkoutTemplate,
      useCases.duplicateWorkoutTemplate,
      useCases.getActiveWorkout,
      useCases.getWorkout,
      useCases.getCompletedWorkoutSeries,
      useCases.startWorkoutFromTemplate,
      useCases.discardWorkout,
      useCases.logSet,
      useCases.updateSet,
      useCases.deleteSet,
      useCases.reorderWorkoutExercises,
      useCases.removeExerciseFromWorkout,
      useCases.completeWorkout,
      useCases.addExerciseToWorkout,
      useCases.replaceWorkoutExercise,
      useCases.listCatalogExercises,
      useCases.listCatalogMuscles,
      useCases.getCatalogExercise,
      useCases.createCustomExercise,
      useCases.updateCustomExercise,
      useCases.deleteCustomExercise,
      useCases.getFocusModePreference,
      useCases.setFocusModePreference,
      useCases.getKeepScreenAwakePreference,
      useCases.setKeepScreenAwakePreference,
    ),
    assistantController: new AssistantController(
      useCases.listConversations,
      useCases.getConversation,
      useCases.createConversation,
      useCases.deleteConversation,
      useCases.applyProposal,
      useCases.discardProposal,
      useCases.uploadAttachment,
      useCases.deleteAttachment,
    ),
    scheduleController: new ScheduleController(
      useCases.getScheduleCalendar,
      useCases.getScheduleWeek,
      useCases.saveScheduleWeek,
      useCases.createScheduleOneOff,
      useCases.removeScheduleOneOff,
      useCases.skipScheduleOccurrence,
      useCases.restoreScheduleOccurrence,
      useCases.moveScheduleOccurrence,
      useCases.swapScheduleOccurrence,
      useCases.startScheduleOccurrence,
      useCases.listScheduleTemplates,
      useCases.getActiveScheduleWorkout,
    ),
  }));

export const buildDependencies = () => dependencyManager.init().getDependencies();

export type Deps = ReturnType<typeof buildDependencies>;
